import { useId, useRef, useState } from "react";
import { pushDataLayer } from "~lib/analytics/datalayer";
import { cx } from "~lib/utils";
import { Input } from "~ui/Input";
import styles from "./ColumnSignup.module.css";
import { fetchWithCSRF } from "~core/fetch";
import { CSRFField } from "~ui/Form/CSRFField";

export function ColumnSignup({
  signup_cta,
  newsletter_name = "today_in_science",
  signupHeaderText = "",
  ...props
}) {
  const id = useId();
  const formRef = useRef(null);
  const [submissionStatus, setSubmissionStatus] = useState("idle");
  const [email, setEmail] = useState(null);

  // State to check if the arrow dropdown should be visible or not
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);

  // State to check if the component should be visible or not
  const [isOpen, setIsOpen] = useState(false);

  /**
   * Toggle the dropdown
   * @param {Event|undefined} event
   */
  function handleDropdownToggle(event) {
    // Set the toggle boolean based on the opposite value of isFlyoutVisible; it's more concise than the usual ternary expression
    setIsDropdownVisible(!isDropdownVisible);
    event?.preventDefault();
  }

  function handleClose(event) {
    setIsOpen(!isOpen);
    event.preventDefault();
  }

  const handleFormSubmit = (e) => {
    e.preventDefault();
    submitForm();
    handleDropdownToggle();
  };

  const submitForm = async () => {
    const formData = new FormData(formRef.current);
    const body = new URLSearchParams(formData);

    const res = fetchWithCSRF(`/platform/api/v2/emailpreference/subscribe/`, {
      method: "POST",
      body,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/x-www-form-urlencoded",
      },
    });

    setEmail(formData.get("email"));
    setSubmissionStatus("loading");

    try {
      await res;
      setSubmissionStatus("success");

      /* Do not use "newsletter-sign-up" DL event for signups. */
      pushDataLayer({
        event: "email-obtained-newsletter-signup",
        user: {
          newsletters: "column_newsletter",
        },
      });
    } catch (error) {
      setSubmissionStatus("error");
    }
  };

  return (
    <div className={cx(styles.columnSignup, isOpen ? styles.closed : "", props.className)}>
      <form
        className={cx(styles.form, styles["status-" + submissionStatus])}
        ref={formRef}
        onSubmit={handleFormSubmit}
      >
        <CSRFField />

        {!props.column && signupHeaderText && (
          <p className={styles.columnSignup__hdr__text}>{signupHeaderText}</p>
        )}
        <button className={cx(styles.columnDropdownBtn)} onClick={handleDropdownToggle}>
          <span
            className={cx(
              submissionStatus === "success" || submissionStatus === "error"
                ? styles.columnDropdownTxtHide
                : styles.columnDropdownTxt,
              isDropdownVisible ? styles.dropdownOpen : "",
            )}
            dangerouslySetInnerHTML={{ __html: signup_cta }}
          ></span>
        </button>

        <div
          className={cx(styles.columnDropdownWrap, isDropdownVisible ? styles.dropdownOpen : "")}
        >
          {/* Email */}
          <div className={styles.inputContainer}>
            <label htmlFor={`email-${id}`} className="sr-only">
              Email Address
            </label>
            <Input
              id={`email-${id}`}
              className={styles.input}
              type="email"
              name="email"
              placeholder="Enter your email"
              required
            />
          </div>

          {/* Consent Checkbox */}
          <div className={styles.inputContainer}>
            <label htmlFor={`consent-${id}`} className={styles.checkboxLabel}>
              <Input
                id={`consent-${id}`}
                className={cx(styles.input, styles.checkbox)}
                type="checkbox"
                name="consent"
                required
              />
              <span>
                I agree my information will be processed in accordance with the Scientific American
                and Springer Nature Limited <a href="/page/privacy-policy">Privacy Policy</a>.
              </span>
            </label>
          </div>

          {/* Newsletter list */}
          <input type="hidden" name="lists" value={newsletter_name} />

          {/* Submit */}
          <button
            className={styles.button}
            type="submit"
            disabled={submissionStatus === "loading" || submissionStatus === "success"}
          >
            Sign Up
          </button>
        </div>
      </form>

      {/* Visually hidden until successful submission */}
      {submissionStatus === "success" && (
        <div className={cx(styles.messageSuccess)}>
          <p>Thank you for signing up!</p>
          <button className={styles.closeButton} onClick={handleClose}>
            <span className={styles.closeButtonTxt}>Close</span>
          </button>
        </div>
      )}
      {submissionStatus === "error" && (
        <div className={cx(styles.messageError)}>
          <p>We've encountered an error. Please refresh the page and try again.</p>
        </div>
      )}
    </div>
  );
}

const IconCheckSuccess = () => (
  <svg width="18" height="14" viewBox="0 0 18 14" fill="none" className={styles.icon}>
    <path
      d="M6.75 12.25L1.75 7.25L0.25 8.75L6.75 15.25L18.25 3.75L16.75 2.25L6.75 12.25Z"
      fill="currentColor"
    />
  </svg>
);

export default ColumnSignup;
